import '@fontsource/roboto';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React from 'react';
import {
  Route,
  Routes,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import './App.css';
import PermissionedRoute from './auth/PermissionedRoute';
import PermissionedSurveyRoute from './auth/PermissionedSurveyRoute';
import ProtectedRoute from './auth/ProtectedRoute';
import SuperuserRoute from './auth/SuperuserRoute';
import ThemeEngineRoute from './auth/ThemeEngineRoute';
import UnprotectedRoute from './auth/UnprotectedRoute';
import useAuth from './auth/useAuth';
import Loading from './components/Loading';
import PageLayout from './components/PageLayout';
import { INTERCOM_API_BASE, INTERCOM_APP_ID, UserRoleType } from './constants';
import { PusherProvider } from './contexts/PusherContext';
// Defaults to weight 400 with normal variant.
import AccountSettings from './pages/AccountSettings';
import AdminPanel from './pages/AdminPanel';
import AuthCallback from './pages/AuthCallback';
import ChangePassword from './pages/ChangePassword';
import Login from './pages/Login';
import MySurveys from './pages/MySurveys';
import NotFound from './pages/NotFound';
import ResponseExplorer from './pages/ResponseExplorer';
import Settings from './pages/Settings';
import SurveyDashboard from './pages/SurveyDashboard';
import SurveyHub from './pages/SurveyHub';
import ThemeEngine from './pages/ThemeEngine';
import Tokens from './pages/Tokens';
import UploadSurvey from './pages/UploadSurvey';
import UploadTranscript from './pages/UploadTranscript';
import ValidateInvitation from './pages/ValidateInvitation';

let SentryRoutes;
const sentryDsn = process.env.REACT_APP_SENTRY_DSN;
const sentryEnvironment = process.env.REACT_APP_ENVIRONMENT;
if (sentryDsn && ['dev', 'staging', 'production'].includes(sentryEnvironment)) {
  Sentry.init({
    dsn: sentryDsn,
    environment: sentryEnvironment,
    tracesSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      new Sentry.BrowserTracing({
        routingInstrumentation: Sentry.reactRouterV6Instrumentation(
          React.useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        ),
      }),
      new Sentry.Replay(),
    ],
    beforeSend(event) {
      // This is a known error in react virtuoso: https://github.com/petyosi/react-virtuoso/issues/254
      // Ignoring it so we don't spam sentry
      const resizeError = event?.exception?.values?.find(({ value }) =>
        value.match(/ResizeObserver loop/i),
      );
      if (resizeError) {
        return null;
      }
      return event;
    },
  });
  SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);
} else {
  SentryRoutes = Routes;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  const { user, isLoading, isAuthenticated } = useAuth();

  if (isAuthenticated) {
    window.Intercom('boot', {
      app_id: INTERCOM_APP_ID,
      api_base: INTERCOM_API_BASE,
      email: user.email,
      created_at: Date.now(),
      hide_default_launcher: false,
    });

    Sentry.setUser({
      username: user.name,
      email: user.email,
    });
  }

  if (isLoading) {
    return <Loading />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <PusherProvider>
        <SentryRoutes>
          {/* Standalone, not under UnprotectedRoute: that guard's redirect races
              the post-sign-in navigation and would swallow the return path. */}
          <Route path="/login" element={<Login />} />
          <Route path="/change-password" element={<ChangePassword />} />
          <Route path="/callback" element={<AuthCallback />} />
          <Route element={<UnprotectedRoute />}>
            <Route path="/invitations/:token" element={<PageLayout />}>
              <Route index element={<ValidateInvitation />} />
            </Route>
          </Route>
          <Route element={<ProtectedRoute />}>
            <Route path="/" element={<PageLayout signupComplete />}>
              <Route path="*" element={<NotFound />} />
              <Route index element={<MySurveys />} />
              <Route element={<PermissionedSurveyRoute />}>
                <Route path="/surveys/:surveyId" element={<SurveyDashboard />} />
                <Route
                  path="/surveys/:surveyId/questions/:questionId/themes/:themeId/responses"
                  element={<ResponseExplorer />}
                />
              </Route>
              <Route element={<ThemeEngineRoute />}>
                <Route
                  element={
                    <PermissionedRoute roles={[UserRoleType.ADMINISTRATOR, UserRoleType.EDITOR]} />
                  }
                >
                  <Route path="/surveys/:surveyId/hub" element={<SurveyHub />} />
                </Route>
              </Route>
              <Route element={<PermissionedRoute roles={[UserRoleType.ADMINISTRATOR]} />}>
                <Route path="/settings/user" element={<Settings />} />
                <Route path="/settings/account" element={<AccountSettings />} />
                <Route path="/settings/tokens" element={<Tokens />} />
                <Route element={<SuperuserRoute />}>
                  <Route path="/lonsdale" element={<AdminPanel />} />
                </Route>
              </Route>
              <Route
                element={
                  <PermissionedRoute roles={[UserRoleType.ADMINISTRATOR, UserRoleType.EDITOR]} />
                }
              >
                <Route path="/surveys/upload" element={<UploadSurvey />} />
                <Route path="/transcripts/upload" element={<UploadTranscript />} />
              </Route>
            </Route>
            <Route path="/" element={<PageLayout showNavBar={false} />}>
              <Route element={<ThemeEngineRoute />}>
                <Route
                  element={
                    <PermissionedRoute roles={[UserRoleType.ADMINISTRATOR, UserRoleType.EDITOR]} />
                  }
                >
                  <Route
                    path="/surveys/:surveyId/questions/:questionId/codeframes/:codeFrameId/themeEngine"
                    element={<ThemeEngine />}
                  />
                </Route>
              </Route>
            </Route>
          </Route>
        </SentryRoutes>
      </PusherProvider>
    </QueryClientProvider>
  );
}

export default App;
